import { useState, useEffect } from 'react';
import { auth, db, storage } from '../lib/firebase';
import { ref, get, query, orderByChild, equalTo, remove, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { toast } from 'sonner';
import { useWallpapers } from './useWallpapers';
import { defaultProfileTemplate } from '../lib/constants';
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import slugify from 'slugify';
import { SubscriptionStatus } from '../types/subscription';

interface Profile {
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  entreprise: string;
  poste: string;
  siteWeb: string;
  adresse: string;
  ville: string;
  codePostal: string;
  pays: string;
  linkedin: string;
  facebook: string;
  x: string;
  github: string;
  instagram: string;
  youtube: string;
  tiktok: string;
  behance: string;
  dribbble: string;
  medium: string;
  discord: string;
  whatsapp: string;
  telegram: string;
  skype: string;
  logoUrl?: string;
  qrCodeUrl?: string;
  wallpaperUrl?: string;
  vcardUrl?: string;
  username?: string;
  isPublic?: boolean;
  qrStyle: {
    fgColor: string;
    bgColor: string;
    style: 'dots' | 'squares' | 'rounded';
  };
  createdAt?: string;
  updatedAt?: string;
  subscription?: {
    status: SubscriptionStatus;
    trialEndDate?: number;
    vcardEnabled: boolean;
  };
}

// Fonction pour générer un slug unique
const generateUniqueSlug = async (baseSlug: string): Promise<string> => {
  let slug = slugify(baseSlug, {
    lower: true,
    strict: true,
    trim: true,
    remove: /[*+~.()'"!:@]/g
  });

  // Vérifier si le slug existe déjà
  const checkSlug = async (testSlug: string): Promise<boolean> => {
    const usernameQuery = query(
      ref(db, 'vcards'),
      orderByChild('username'),
      equalTo(testSlug)
    );
    const snapshot = await get(usernameQuery);
    return snapshot.exists();
  };

  // Si le slug existe, ajouter un numéro à la fin
  let counter = 1;
  let finalSlug = slug;
  while (await checkSlug(finalSlug)) {
    finalSlug = `${slug}-${counter}`;
    counter++;
  }

  return finalSlug;
};

// Fonction pour générer un username par défaut
const generateDefaultUsername = async (profile: Profile): Promise<string> => {
  // Si un username existe déjà, le retourner
  if (profile.username) return profile.username;

  let baseSlug = '';
  
  // 1. Essayer d'utiliser l'entreprise
  if (profile.entreprise) {
    baseSlug = profile.entreprise;
  } 
  // 2. Sinon essayer d'utiliser prénom + nom
  else if (profile.prenom || profile.nom) {
    baseSlug = [profile.prenom, profile.nom].filter(Boolean).join('-');
  }
  
  // 3. Si toujours pas de slug, utiliser un timestamp
  if (!baseSlug) {
    const timestamp = Date.now();
    baseSlug = `user${timestamp}`;
    return baseSlug; // Pas besoin de vérifier l'unicité pour un timestamp
  }

  // Pour les slugs basés sur le nom/entreprise, vérifier l'unicité
  return generateUniqueSlug(baseSlug);
};

// Fonction pour vérifier si l'essai est toujours valide
const isTrialValid = (trialEndDate?: number): boolean => {
  if (!trialEndDate) return false;
  return Date.now() < trialEndDate;
};

export function useProfile() {
  const [profile, setProfile] = useState<Profile>(defaultProfileTemplate);
  const [loading, setLoading] = useState(true);
  const { countWallpapers } = useWallpapers();

  useEffect(() => {
    const loadProfile = async () => {
      if (!auth.currentUser) return;

      try {
        // Charger le profil
        const profileRef = ref(db, `vcards/${auth.currentUser.uid}`);
        const snapshot = await get(profileRef);
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          // Générer l'URL vCard si elle n'existe pas
          if (!data.vcardUrl) {
            data.vcardUrl = `https://us-central1-blog-meryl.cloudfunctions.net/vcard/${auth.currentUser.uid}`;
          }

          // Vérifier et mettre à jour le statut d'abonnement si nécessaire
          if (!data.subscription) {
            data.subscription = {
              status: 'trial' as SubscriptionStatus,
              trialEndDate: Date.now() + (7 * 24 * 60 * 60 * 1000), // 7 jours d'essai
              vcardEnabled: true
            };
          } else if (data.subscription.status === 'trial' && !isTrialValid(data.subscription.trialEndDate)) {
            data.subscription.status = 'expired';
            data.subscription.vcardEnabled = false;
            // Mettre à jour dans Firebase
            await set(profileRef, data);
          }

          // Préserver les données existantes et mettre à jour avec les nouvelles données
          const existingProfile = profile || defaultProfileTemplate;
          setProfile({ 
            ...existingProfile,
            ...data,
            logoUrl: data.logoUrl || existingProfile.logoUrl, // Préserver le logo existant si pas dans data
            qrCodeUrl: data.qrCodeUrl || existingProfile.qrCodeUrl,
            wallpaperUrl: data.wallpaperUrl || existingProfile.wallpaperUrl
          });
        } else {
          // Si le profil n'existe pas, utiliser le template par défaut avec un essai de 7 jours
          const newProfile = {
            ...defaultProfileTemplate,
            subscription: {
              status: 'trial' as SubscriptionStatus,
              trialEndDate: Date.now() + (7 * 24 * 60 * 60 * 1000),
              vcardEnabled: true
            }
          };
          setProfile(newProfile);
        }
      } catch (error) {
        console.error('Erreur lors du chargement du profil:', error);
        toast.error('Erreur lors du chargement du profil');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [auth.currentUser]);

  const saveProfile = async (updatedProfile: Profile) => {
    if (!auth.currentUser) {
      toast.error('Vous devez être connecté pour sauvegarder votre profil');
      return;
    }

    const toastId = toast.loading('Sauvegarde en cours...');

    try {
      // Générer un username par défaut si non défini
      if (!updatedProfile.username) {
        updatedProfile.username = await generateDefaultUsername(updatedProfile);
      }

      // Vérifier si le username est déjà pris par un autre utilisateur
      if (updatedProfile.username !== profile.username) {
        const usernameQuery = query(
          ref(db, 'vcards'),
          orderByChild('username'),
          equalTo(updatedProfile.username)
        );
        const snapshot = await get(usernameQuery);
        if (snapshot.exists()) {
          const [existingUid] = Object.keys(snapshot.val());
          if (existingUid !== auth.currentUser.uid) {
            toast.error('Ce nom d\'utilisateur est déjà pris', { id: toastId });
            return;
          }
        }
      }

      // Récupérer le profil existant pour préserver createdAt si il existe
      const profileRef = ref(db, `vcards/${auth.currentUser.uid}`);
      const existingSnapshot = await get(profileRef);
      const existingProfile = existingSnapshot.exists() ? existingSnapshot.val() : null;

      // Ajouter ou mettre à jour les timestamps
      const now = new Date().toISOString();

      // S'assurer qu'aucune valeur n'est undefined
      const cleanProfile = {
        ...defaultProfileTemplate, // Base avec les valeurs par défaut
        ...updatedProfile, // Nouvelles valeurs
        logoUrl: updatedProfile.logoUrl || '',
        qrCodeUrl: updatedProfile.qrCodeUrl || '',
        wallpaperUrl: updatedProfile.wallpaperUrl || '',
        vcardUrl: updatedProfile.vcardUrl || '',
        updatedAt: now,
        createdAt: existingProfile?.createdAt || now
      };

      // Sauvegarder le profil
      await set(profileRef, cleanProfile);
      setProfile(cleanProfile);
      toast.success('Profil sauvegardé avec succès', { id: toastId });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde du profil', { id: toastId });
      throw error;
    }
  };

  // Fonction pour uploader un logo
  const uploadLogo = async (file: File): Promise<string> => {
    if (!auth.currentUser) throw new Error('Non authentifié');
    
    const userId = auth.currentUser.uid;
    const fileExt = file.name.split('.').pop();
    const timestamp = Date.now();
    const logoRef = storageRef(storage, `${userId}/logos/logo-${timestamp}.${fileExt}`);
    
    try {
      // Upload du fichier
      await uploadBytes(logoRef, file);
      const url = await getDownloadURL(logoRef);
      
      // Récupérer le profil actuel pour s'assurer d'avoir toutes les propriétés
      const profileRef = ref(db, `vcards/${userId}`);
      const snapshot = await get(profileRef);
      const currentProfile = snapshot.exists() ? snapshot.val() : { ...defaultProfileTemplate };
      
      // Mettre à jour le profil avec la nouvelle URL du logo
      const updatedProfile = {
        ...currentProfile,
        logoUrl: url,
        updatedAt: new Date().toISOString()
      };

      // Sauvegarder le profil complet et attendre la fin de la sauvegarde
      await saveProfile(updatedProfile);
      
      // Retourner l'URL seulement après que tout soit sauvegardé
      return url;
    } catch (error) {
      console.error('Erreur lors de l\'upload du logo:', error);
      throw error;
    }
  };

  // Fonction pour uploader un QR code
  const uploadQRCode = async (file: File | Blob): Promise<string> => {
    if (!auth.currentUser) throw new Error('Non authentifié');
    
    const userId = auth.currentUser.uid;
    // Si c'est un Blob sans nom de fichier, on utilise .png par défaut
    const fileExt = file instanceof File ? file.name.split('.').pop() : 'png';
    const qrCodeRef = storageRef(storage, `${userId}/qrcodes/qrcode.${fileExt}`);
    
    await uploadBytes(qrCodeRef, file);
    const url = await getDownloadURL(qrCodeRef);
    return url;
  };

  // Fonction pour uploader un wallpaper
  const uploadWallpaperBlob = async (file: File): Promise<string> => {
    if (!auth.currentUser) throw new Error('Non authentifié');
    
    const userId = auth.currentUser.uid;
    const fileExt = file.name.split('.').pop();
    const wallpaperRef = storageRef(storage, `wallpapers/${userId}/wallpaper.${fileExt}`);
    
    await uploadBytes(wallpaperRef, file);
    const url = await getDownloadURL(wallpaperRef);
    return url;
  };

  const deleteAccount = async (password: string) => {
    if (!auth.currentUser) {
      toast.error('Vous devez être connecté pour supprimer votre compte');
      return false;
    }

    const userId = auth.currentUser.uid;
    const toastId = toast.loading('Suppression du compte en cours...');

    try {
      // 1. Ré-authentifier l'utilisateur
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email!,
        password
      );
      await reauthenticateWithCredential(auth.currentUser, credential);

      // 2. Supprimer le profil de la base de données
      const profileRef = ref(db, `vcards/${userId}`);
      const profileSnapshot = await get(profileRef);
      const profileData = profileSnapshot.val();

      // 3. Supprimer le username des usernames
      if (profileData?.username) {
        try {
          await remove(ref(db, `usernames/${profileData.username}`));
        } catch (error) {
          console.error('Erreur lors de la suppression du username:', error);
        }
      }

      // 4. Supprimer les fichiers de stockage
      const deleteFileIfExists = async (path: string) => {
        try {
          const fileRef = storageRef(storage, path);
          await deleteObject(fileRef);
        } catch (error: any) {
          console.error(`Erreur lors de la suppression de ${path}:`, error);
        }
      };

      // Supprimer les fichiers avec les bons chemins
      await Promise.all([
        deleteFileIfExists(`logos/${userId}`),
        deleteFileIfExists(`qrcodes/${userId}`),
        deleteFileIfExists(`wallpapers/${userId}`)
      ]);

      // 5. Supprimer le profil
      await remove(profileRef);

      // 6. Supprimer le compte Firebase Auth
      await deleteUser(auth.currentUser);
      
      toast.success('Compte supprimé avec succès', { id: toastId });
      
      // 7. Rediriger vers la page de connexion
      window.location.href = '/login';

      return true;
    } catch (error: any) {
      console.error('Erreur lors de la suppression du compte:', error);
      
      let errorMessage = 'Erreur lors de la suppression du compte';
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Mot de passe incorrect';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Pour des raisons de sécurité, veuillez vous déconnecter et vous reconnecter avant de supprimer votre compte';
      }
      
      toast.error(errorMessage, { id: toastId });
      throw error;
    }
  };

  return {
    profile,
    setProfile,
    loading,
    saveProfile,
    uploadLogo,
    uploadWallpaperBlob,
    uploadQRCode,
    deleteAccount
  };
}